import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import studentService from '../../services/api/student';
import { useQuery } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  User, 
  Mail, 
  Calendar, 
  MapPin, 
  BookOpen, 
  Award,
  Clock,
  CheckCircle,
  Target,
  GraduationCap,
  ShieldCheck,
  Smartphone,
  BadgeCheck,
  Sparkles,
  ArrowRight,
  MonitorPlay,
  Zap,
  Activity,
  MessageSquare,
  ExternalLink,
  History
} from 'lucide-react';
import { getImageUrl } from '../../config/apiConfig';


const StudentProfile = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  // جلب بيانات الطالب (المنطق الأصلي كاملاً مع Debug Logs)
  const { data: studentData, isLoading, error } = useQuery(
    ['student-profile', studentId],
    () => studentService.getStudentProfile(studentId).then(data => {
      return data;
    }),
    { enabled: !!studentId, retry: 1 }
  );

  const student = studentData?.student;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-black animate-pulse uppercase tracking-widest text-xs">جاري جلب ملف الطالب التعليمي...</p>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-white rounded-[3rem] shadow-2xl p-12 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-red-500"></div>
          <User size={64} className="text-red-200 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-4">تعذر العثور على الطالب</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">يبدو أن رابط الملف غير صحيح أو أن الطالب لم يعد مسجلاً في الكورسات الخاصة بك.</p>
          <button 
            onClick={() => navigate('/teacher/students')}
            className="bg-primary-600 text-white px-10 py-3 rounded-2xl font-black hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto shadow-lg"
          >
            <ArrowRight size={18} /> العودة لقائمة طلابي
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-10 min-h-screen bg-gray-50/30">
      
      {/* Premium Header Hero Card - Fixed Layout for Visibility */}
      <div className="bg-white rounded-[2.5rem] sm:rounded-[3.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden relative group transition-all duration-500">
        <div className="relative">
          {/* Header Cover - Fixed height & Gradient */}
          <div className="h-32 sm:h-48 lg:h-64 bg-gradient-to-br from-indigo-700 via-primary-600 to-purple-800 relative">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            
            {/* Status Floating Badge */}
            <div className="absolute top-8 left-8 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-2xl shadow-2xl z-10">
              <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping"></div>
              <span className="text-[10px] font-black text-white uppercase tracking-widest">متفاعل حالياً</span>
            </div>
          </div>
          
          {/* Content Area - Logic Fix: PT-36 ensures name is visible below the cover on large screens */}
          <div className="px-6 sm:px-10 lg:px-16 pb-12 relative">
            <div className="flex flex-col lg:flex-row gap-10 items-center lg:items-end -mt-16 sm:-mt-24 lg:-mt-32">
              
              {/* Student Avatar Wrapper */}
              <div className="relative group z-20">
                <div className="absolute -inset-2 bg-gradient-to-tr from-primary-600 to-indigo-600 rounded-[2.8rem] blur opacity-25 group-hover:opacity-50 transition-opacity duration-500"></div>
                <div className="relative h-32 w-32 sm:h-44 sm:w-44 lg:h-56 lg:w-56 rounded-[2.5rem] bg-white border-[8px] border-white shadow-2xl flex items-center justify-center text-primary-600 font-black text-5xl overflow-hidden">
                  {student.avatar ? (
                    <img src={getImageUrl(student.avatar)} alt={student.name} className="w-full h-full object-cover" />
                  ) : (
                    student.name?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="absolute -bottom-2 -right-2 bg-emerald-500 text-white p-3 rounded-2xl border-4 border-white shadow-xl z-30">
                  <BadgeCheck size={24} />
                </div>
              </div>

              {/* Info Text Area - Large padding top fix for large screens */}
              <div className="flex-1 space-y-6 pt-4 lg:pt-36 w-full text-center lg:text-right relative z-10">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                  <div className="space-y-4">
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight flex flex-wrap items-center justify-center lg:justify-start gap-4">
                      {student.name}
                      <ShieldCheck className="text-blue-500 fill-blue-50" size={28} />
                    </h1>
                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                        <div className="bg-primary-50 text-primary-700 px-5 py-2 rounded-2xl font-black text-xs uppercase tracking-widest border border-primary-100 flex items-center gap-2">
                          <GraduationCap size={16} />
                          {student.grade_name || 'طالب ثانوي'}
                        </div>
                        <div className="bg-gray-100 text-gray-500 px-5 py-2 rounded-2xl font-black text-xs uppercase tracking-widest border border-gray-200 flex items-center gap-2">
                          <MapPin size={16} />
                          {student.address || 'غير محدد'}
                        </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-end">
                    <button 
                      onClick={() => navigate('/teacher/questions')}
                      className="bg-primary-600 text-white px-8 py-4 rounded-[1.8rem] font-black text-sm shadow-2xl shadow-primary-200 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-3"
                    >
                      <MessageSquare size={20} />
                      إرسال رسالة
                    </button>
                    <button 
                      onClick={() => navigate('/teacher/students')}
                      className="bg-gray-50 text-gray-700 px-8 py-4 rounded-[1.8rem] font-black text-sm border border-gray-100 hover:bg-white hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    >
                      <ArrowRight size={20} />
                      قائمة الطلاب
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Statistics Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { label: 'الكورسات المشتركة', value: student?.stats?.coursesCount || 0, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'الدروس المكتملة', value: student?.stats?.completedLessons || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'إجمالي الساعات', value: student?.stats?.studyHours || 0, icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'شهادات الإنجاز', value: student?.stats?.certificatesCount || 0, icon: Award, color: 'text-amber-500', bg: 'bg-amber-50' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex items-center gap-6 group hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <div className={`p-5 ${stat.bg} ${stat.color} rounded-3xl group-hover:scale-110 transition-transform shadow-inner`}>
              <stat.icon size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-1">{stat.label}</p>
              <p className="text-4xl font-black text-gray-900 tracking-tighter leading-none">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-20">
        
        {/* Left Column: Student Contact Card (4 Columns) */}
        <div className="lg:col-span-4 space-y-8 animate-in fade-in slide-in-from-right duration-700">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 transition-all hover:shadow-xl">
            <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                <ShieldCheck size={22} className="text-primary-600" /> معلومات التواصل
            </h3>
            
            <div className="space-y-6">
              {[
                { label: 'البريد الإلكتروني', value: student.email, icon: Mail, color: 'text-blue-500', bg: 'bg-blue-50' },
                { label: 'رقم الهاتف', value: student.phone || 'غير متاح', icon: Smartphone, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: 'الموقع الجغرافي', value: student.address || 'غير محدد', icon: MapPin, color: 'text-rose-500', bg: 'bg-rose-50' },
                { label: 'تاريخ الانضمام للمنصة', value: getArabicDate(student.created_at), icon: Calendar, color: 'text-amber-500', bg: 'bg-amber-50' }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-5 p-4 rounded-3xl bg-gray-50/50 border border-transparent hover:bg-white hover:border-gray-100 transition-all group/item">
                  <div className={`p-3 bg-white rounded-2xl shadow-sm ${item.color} group-hover/item:scale-110 transition-transform`}>
                    <item.icon size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{item.label}</p>
                    <p className="text-sm font-bold text-gray-800 truncate">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-gray-50">
                <div className="flex items-center justify-between p-4 bg-primary-50 rounded-[1.5rem] border border-primary-100">
                    <div className="flex items-center gap-3">
                        <Zap size={18} className="text-primary-600 animate-pulse" />
                        <span className="text-xs font-black text-primary-700 uppercase">حالة الطالب</span>
                    </div>
                    <span className="bg-white px-3 py-1 rounded-full text-[10px] font-black text-emerald-600 shadow-sm border border-emerald-50">نـشـط</span>
                </div>
            </div>
          </div>

          {/* Teacher Note Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10 space-y-6">
                <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl shadow-primary-900/50">
                    <Sparkles size={24} />
                </div>
                <h4 className="text-2xl font-black mb-2 leading-tight tracking-tight">ملاحظات <br /> المدرس الخاصة</h4>
                <p className="text-gray-400 text-sm font-medium leading-relaxed">بإمكانك إضافة ملاحظات خاصة حول تقدم هذا الطالب لن يراها أحد غيرك لتسهيل متابعة مستواه الدراسي.</p>
                <button className="w-full bg-white text-gray-900 font-black py-4 rounded-2xl hover:bg-primary-50 transition-all flex items-center justify-center gap-2 transform active:scale-95 shadow-xl">
                    إضافة ملاحظة <ArrowRight size={18} className="rotate-180" />
                </button>
            </div>
          </div>
        </div>

        {/* Right Column: Activity Feed (8 Columns) */}
        <div className="lg:col-span-8 space-y-8 animate-in fade-in slide-in-from-left duration-700">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 transition-all hover:shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-2 h-full bg-primary-600 opacity-20 transition-opacity"></div>
            
            <div className="flex items-center justify-between mb-10">
                <h3 className="text-2xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
                    <History size={24} className="text-primary-600" />
                    تتبع النشاط التعليمي الأخير
                </h3>
                <div className="flex items-center gap-2 bg-gray-50 px-4 py-1.5 rounded-full border border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                    <Activity size={12} className="text-emerald-500" /> مباشر
                </div>
            </div>
            
            <div className="space-y-6">
              {student?.recentActivity?.length > 0 ? (
                student.recentActivity.map((activity, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col sm:flex-row items-center justify-between p-6 bg-gray-50/50 rounded-[2rem] border border-gray-50 hover:bg-white hover:shadow-lg hover:border-primary-100 transition-all duration-300 group/item gap-6"
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-primary-600 shadow-sm border border-gray-100 group-hover/item:scale-110 group-hover/item:bg-primary-600 group-hover/item:text-white transition-all duration-500">
                        <MonitorPlay size={26} />
                      </div>
                      <div className="space-y-1">
                        <p className="text-lg font-black text-gray-800 leading-tight group-hover/item:text-primary-600 transition-colors">{activity.title}</p>
                        <div className="flex items-center gap-3">
                            <span className="bg-primary-50 text-primary-600 text-[9px] font-black px-2.5 py-0.5 rounded-lg border border-primary-100 uppercase tracking-widest">إتمام بنجاح</span>
                            <p className="text-xs font-bold text-gray-400">{activity.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-left flex-shrink-0 flex flex-col items-center sm:items-end gap-2">
                        <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 bg-white px-4 py-1.5 rounded-xl border border-gray-100 shadow-sm">
                            <Calendar size={14} className="text-primary-500" />
                            {getArabicDate(activity.date)}
                        </div>
                        <button className="text-primary-600 font-black text-[10px] uppercase flex items-center gap-1 hover:underline">
                            عرض التفاصيل <ExternalLink size={10} />
                        </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-24 bg-gray-50/50 rounded-[3rem] border-2 border-dashed border-gray-200">
                  <div className="w-20 h-20 bg-white rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100">
                    <Activity className="text-gray-300" size={40} />
                  </div>
                  <h4 className="text-xl font-black text-gray-900 mb-2 tracking-tight uppercase">لا يوجد نشاط مسجل</h4>
                  <p className="text-gray-400 max-w-xs mx-auto font-medium">لم يقم الطالب بأي نشاط تعليمي (مشاهدة دروس أو حل اختبارات) خلال الفترة الماضية.</p>
                </div>
              )}
            </div>

            {/* Performance Summary Banner */}
            <div className="mt-12 p-8 bg-gradient-to-br from-primary-600 to-indigo-700 rounded-[2.5rem] shadow-xl text-white relative overflow-hidden group/banner">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl"></div>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-8 relative z-10">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/30 group-hover/banner:rotate-12 transition-transform duration-500">
                            <Target size={32} />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-xl font-black leading-none">معدل التزام الطالب</h4>
                            <p className="text-primary-100 text-sm font-medium">يلتزم الطالب بمشاهدة الدروس فور نزولها بنسبة 92%</p>
                        </div>
                    </div>
                    <Link to={`/teacher/reports`} className="bg-white text-primary-700 px-8 py-3 rounded-2xl font-black text-xs hover:bg-primary-50 transition-all shadow-xl active:scale-95">
                        التقرير المفصل
                    </Link>
                </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StudentProfile;